import { axiosPrivate } from '../interceptors/Api.interceptor';
import type { AxiosHeaders, AxiosResponse } from 'axios';
import { NotificationStore } from '../stores/notification.stores';
import { NotificationEnum } from '../interfaces/Notification';
import axios from 'axios';
import { get } from 'svelte/store';
import { _ } from './Translation.service';

export class ApiService {
	baseUrl: string;
	apiKey: string;
	skipAuth: boolean;

	constructor(apiBaseUrl?: string, apiKey?: string, skipAuth = false) {
		this.baseUrl = apiBaseUrl ?? import.meta.env.VITE_API_BASE_PATH;
		this.apiKey = apiKey ?? import.meta.env.VITE_X_API_KEY;
		axiosPrivate.defaults.headers.common['Content-type'] = 'application/json';
		this.skipAuth = skipAuth;
	}

	// This is the approved way to typecheck Axios errors
	// https://github.com/axios/axios/issues/3612
	static handleApiError(error: unknown, notifyOnError = true) {
		if (axios.isAxiosError(error)) {
			const apiError = error?.response?.data;
			if (notifyOnError) {
				NotificationStore.set({
					title: apiError?.error ?? get(_)('error_title'),
					description: apiError?.error_description ?? get(_)('error_standard'),
					type: NotificationEnum.Danger,
					show: true
				});
			}
		}
		return Promise.reject(error);
	}

	public get<T, TFull extends boolean = false>(
		path: string,
		customHeaders?: AxiosHeaders,
		params?: URLSearchParams,
		fullResponse?: TFull,
		notifyOnError = true
	): Promise<TFull extends true ? AxiosResponse<T> : T> {
		return axiosPrivate
			.get(this.baseUrl + path, {
				params: params,
				skipAuth: this.skipAuth,
				headers: {
					'x-api-key': this.apiKey,
					...customHeaders
				}
			})
			.then(
				(response: AxiosResponse<T>) =>
					(fullResponse ? response : response.data) as TFull extends true ? AxiosResponse<T> : T
			)
			.catch((error) => ApiService.handleApiError(error, notifyOnError));
	}

	public post<T, F>(path: string, data?: F, customHeaders?: AxiosHeaders): Promise<T> {
		return axiosPrivate
			.post(this.baseUrl + path, data, {
				skipAuth: this.skipAuth,
				headers: {
					'x-api-key': this.apiKey,
					...customHeaders
				}
			})
			.then((response: AxiosResponse) => response.data)
			.catch(ApiService.handleApiError);
	}

	public postWithoutBody<T>(path: string, customHeaders?: AxiosHeaders): Promise<T> {
		return axiosPrivate
			.post(this.baseUrl + path, null, {
				headers: {
					'x-api-key': this.apiKey,
					...customHeaders
				}
			})
			.then((response: AxiosResponse) => response.data)
			.catch(ApiService.handleApiError);
	}

	public patch<T, F>(path: string, data: F, customHeaders?: AxiosHeaders): Promise<T> {
		return axiosPrivate
			.patch(this.baseUrl + path, data, {
				headers: {
					'x-api-key': this.apiKey,
					...customHeaders
				}
			})
			.then((response: AxiosResponse) => response.data)
			.catch(ApiService.handleApiError);
	}

	public delete<T>(path: string, customHeaders?: AxiosHeaders): Promise<T> {
		return axiosPrivate
			.delete(this.baseUrl + path, {
				headers: {
					'x-api-key': this.apiKey,
					...customHeaders
				}
			})
			.then((response: AxiosResponse) => response.data)
			.catch(ApiService.handleApiError);
	}

	public patchWithoutBody<T>(path: string, customHeaders?: AxiosHeaders): Promise<T> {
		return axiosPrivate
			.patch(this.baseUrl + path, null, {
				headers: {
					'x-api-key': this.apiKey,
					...customHeaders
				}
			})
			.then((response: AxiosResponse) => response.data)
			.catch(ApiService.handleApiError);
	}

	public put<T>(path: string, customHeaders?: AxiosHeaders): Promise<T> {
		return axiosPrivate
			.put(this.baseUrl + path, null, {
				headers: {
					'x-api-key': this.apiKey,
					...customHeaders
				}
			})
			.then((response: AxiosResponse) => response.data)
			.catch(ApiService.handleApiError);
	}

	public putWithBody<T, F>(path: string, data?: F, customHeaders?: AxiosHeaders): Promise<T> {
		return axiosPrivate
			.put(this.baseUrl + path, data, {
				headers: {
					'x-api-key': this.apiKey,
					...customHeaders
				}
			})
			.then((response: AxiosResponse) => response.data)
			.catch(ApiService.handleApiError);
	}
}
